import React, { useState } from "react";
import {
  AppBar,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
  Toolbar,
  useMediaQuery,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useHistory } from "react-router-dom";
import { createPaymentType } from "apis/paymentAPIs";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  showSuccessSnackbar,
  showErrorSnackbar,
} from "store/actions/snackbarMessage";

import { useDispatch } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";

import { noWheelInputProps } from "util/utils";
import {
  isValidSurchargeRate,
  parseSurchargeRate,
  SURCHARGE_RATE_ERROR,
} from "../../surchargeUtils";

const useStyles = makeStyles((theme) => ({
  toolbar: {
    gap: theme.spacing(1),
    paddingLeft: theme.spacing(2),
  },
  card: {
    margin: theme.spacing(2),
  },
  customPaymentType: {
    marginTop: theme.spacing(2),
  },
  switchField: {
    // Line the switch up with the input beside it rather than the top of the row.
    display: "flex",
    alignItems: "center",
    // Offset the helper text under the input so both read as one row.
    paddingBottom: theme.spacing(2.5),
  },
  actions: {
    marginTop: theme.spacing(1),
  },
}));

const PAYMENT_TYPES = [
  { value: "OBPayments", label: "OB Payments" },
  { value: "CASH", label: "Cash" },
  { value: "VISAMastercard", label: "VISA/Master" },
  { value: "EFTPOS", label: "EFTPOS" },
  { value: "AMEX", label: "AMEX" },
  { value: "WECHATPAY", label: "WeChat Pay" },
  { value: "ALIPAY", label: "AliPay" },
  { value: "UnionPay", label: "UnionPay" },
  { value: "InternationalCard", label: "International Card" },
  { value: "Other", label: "Other" },
];

const PaymentType = () => {
  const classes = useStyles();
  const history = useHistory();
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [paymentType, setPaymentType] = useState("");
  const [customPaymentType, setCustomPaymentType] = useState("");
  const [surchargeRate, setSurchargeRate] = useState(0);
  const [description, setDescription] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [errors, setErrors] = useState({});
  const [sort, setSort] = useState(99);

  const dispatch = useDispatch();
  const showSuccessMessage = (msg) => dispatch(showSuccessSnackbar(msg));
  const showErrorMessage = (msg) => dispatch(showErrorSnackbar(msg));

  const clearError = (field) =>
    setErrors((current) => ({ ...current, [field]: undefined }));

  const handlePaymentTypeChange = (event) => {
    const value = event.target.value;
    setPaymentType(value);
    clearError("paymentType");
    if (value === "OBPayments") {
      setSurchargeRate(0);
      setDescription("OB Payments");
    }
  };

  const handleCustomPaymentTypeChange = (event) => {
    setCustomPaymentType(event.target.value);
    clearError("paymentType");
  };

  const handleSurchargeRateChange = (event) => {
    setSurchargeRate(event.target.value);
    clearError("surchargeRate");
  };

  const handleDescriptionChange = (event) => {
    setDescription(event.target.value);
  };

  const handleSortChange = (event) => {
    setSort(event.target.value);
    clearError("sort");
  };

  const handleEnabledChange = (event) => {
    setEnabled(event.target.checked);
  };

  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: createPaymentType,
    onSuccess: () => {
      queryClient.invalidateQueries("paymentTypes");
      showSuccessMessage("Payment type created successfully");
      history.push("/paymentTypes");
    },
    onError: (error) => {
      showErrorMessage(error.message);
    },
  });

  const handleSubmit = (event) => {
    event.preventDefault();

    const errors = {};

    if (!paymentType && !customPaymentType) {
      errors.paymentType = "Payment type is required";
    }

    if (!isValidSurchargeRate(surchargeRate)) {
      errors.surchargeRate = SURCHARGE_RATE_ERROR;
    }

    // check sort is a number
    if (sort && isNaN(sort)) {
      errors.sort = "Sort must be a number";
    }

    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    const selectedPaymentType = customPaymentType || paymentType;
    const paymentTypeData = {
      paymentType: selectedPaymentType,
      surchargeRate: parseSurchargeRate(surchargeRate),
      description,
      sort,
      enabled,
    };

    mutation.mutate(paymentTypeData);
  };

  return (
    <>
      <AppBar
        position="static"
        style={{
          backgroundColor: "transparent",
          boxShadow: "none",
          marginTop: "10px",
        }}
      >
        <Toolbar className={classes.toolbar}>
          <Button variant="outlined" onClick={() => history.goBack()}>
            Back
          </Button>
          <Button
            variant="outlined"
            onClick={() => history.push("/myrestaurant")}
          >
            My Restaurant
          </Button>
        </Toolbar>
      </AppBar>
      <Card className={classes.card}>
        <CardHeader
          title="Payment Type"
          subheader="Create a payment type for this restaurant"
        />
        <form onSubmit={handleSubmit} noValidate>
          <CardContent>
            <Grid container spacing={isSmallScreen ? 2 : 3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Payment Type"
                  margin="dense"
                  variant="outlined"
                  value={paymentType}
                  onChange={handlePaymentTypeChange}
                  error={!!errors.paymentType}
                  helperText={errors.paymentType}
                >
                  {PAYMENT_TYPES.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                {paymentType === "Other" && (
                  <TextField
                    className={classes.customPaymentType}
                    fullWidth
                    label="Custom payment type"
                    margin="dense"
                    variant="outlined"
                    value={customPaymentType}
                    onChange={handleCustomPaymentTypeChange}
                    placeholder="Enter custom payment type"
                  />
                )}
              </Grid>
              {paymentType !== "OBPayments" && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Surcharge Rate"
                    margin="dense"
                    variant="outlined"
                    value={surchargeRate}
                    onChange={handleSurchargeRateChange}
                    error={!!errors.surchargeRate}
                    helperText={
                      errors.surchargeRate ||
                      "Use a negative rate to discount this payment type, e.g. -5."
                    }
                    inputProps={{ ...noWheelInputProps, step: "any" }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  margin="dense"
                  variant="outlined"
                  value={description}
                  onChange={handleDescriptionChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Sort Order"
                  margin="dense"
                  variant="outlined"
                  value={sort}
                  onChange={handleSortChange}
                  error={!!errors.sort}
                  helperText={
                    errors.sort || "Smaller numbers are displayed first."
                  }
                  inputProps={{ ...noWheelInputProps, step: "1" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} className={classes.switchField}>
                <FormControlLabel
                  control={
                    <Switch checked={enabled} onChange={handleEnabledChange} />
                  }
                  label={enabled ? "Enabled" : "Disabled"}
                />
              </Grid>
              <Grid item xs={12} className={classes.actions}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={mutation.isLoading}
                  startIcon={
                    mutation.isLoading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : null
                  }
                >
                  {mutation.isLoading ? "Saving..." : "Save"}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </form>
      </Card>
    </>
  );
};

export default PaymentType;
